// src/context/LanguageContext.jsx
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import translations, { DEFAULT_LANG, LANGUAGES } from "../i18n/translations";

const STORAGE_KEY = "kaida_lang";
const CODES = LANGUAGES.map((l) => l.code);

const LanguageContext = createContext(null);

/** Saved choice wins; otherwise fall back to the browser, then Albanian. */
const detect = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && CODES.includes(saved)) return saved;
  } catch {
    /* private mode — carry on */
  }

  const nav = (navigator.language || "").slice(0, 2).toLowerCase();
  if (CODES.includes(nav)) return nav;
  return DEFAULT_LANG;
};

/** Walks "checkout.errName" style keys. Returns undefined if any hop misses. */
const pick = (dict, path) =>
  path
    .split(".")
    .reduce((acc, key) => (acc == null ? undefined : acc[key]), dict);

/** Replaces {{name}} placeholders. Values are inserted as plain text. */
const interpolate = (str, vars) => {
  if (!vars) return str;
  return str.replace(/\{\{(\w+)\}\}/g, (match, key) =>
    vars[key] === undefined || vars[key] === null ? match : String(vars[key]),
  );
};

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(detect);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      /* ignore */
    }
    // screen readers and browser translate prompts both rely on this
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((next) => {
    if (CODES.includes(next)) setLangState(next);
  }, []);

  /**
   * t("cart.total") or t("cart.maxStock", { count: 3 }).
   *
   * Falls back to Albanian for any key the active language is missing, so a
   * partial translation shows real words rather than a raw key. If the key
   * doesn't exist at all it returns the key itself — visible in dev, and
   * never a blank space in production.
   */
  const t = useCallback(
    (key, vars) => {
      const value =
        pick(translations[lang], key) ?? pick(translations[DEFAULT_LANG], key);

      if (value === undefined) {
        if (process.env.NODE_ENV === "development") {
          console.warn(`[i18n] missing key: ${key}`);
        }
        return key;
      }

      return typeof value === "string" ? interpolate(value, vars) : value;
    },
    [lang],
  );

  const value = useMemo(
    () => ({ lang, setLang, t, languages: LANGUAGES }),
    [lang, setLang, t],
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLang = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLang must be used inside <LanguageProvider>");
  return ctx;
};
